import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  #youtubePlayer;
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.hasAttribute('data-defer-autoplay')) {
      this.#warmDeferredPoster(signal);
      this.#observeDeferredAutoplay(signal);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#youtubePlayer?.destroy();
    this.#youtubePlayer = undefined;
  }

  /**
   * Starts the lightweight poster request well before the video reaches the viewport.
   * This keeps lazy loading while avoiding an empty media frame during fast scrolling.
   * @param {AbortSignal} signal
   */
  #warmDeferredPoster(signal) {
    const poster = this.querySelector('.deferred-media__poster-image');
    if (!(poster instanceof HTMLImageElement)) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const loadPoster = () => {
      poster.loading = 'eager';
      poster.fetchPriority = 'low';
    };

    if (!('IntersectionObserver' in window)) {
      loadPoster();
      return;
    }

    const posterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        posterObserver.disconnect();
        loadPoster();
      },
      { rootMargin: isMobile ? '1000px 0px' : '1400px 0px', threshold: 0 }
    );

    posterObserver.observe(this);
    signal.addEventListener('abort', () => posterObserver.disconnect(), { once: true });
  }

  /**
   * Loads autoplay media shortly before it reaches the viewport.
   * The poster remains an accessible click fallback until loading completes.
   * @param {AbortSignal} signal
   */
  #observeDeferredAutoplay(signal) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const playRatio = isMobile ? 0.25 : 0.15;
    const pauseRatio = isMobile ? 0.12 : 0.08;
    const pauseDelay = isMobile ? 260 : 180;
    let visibleRatio = 0;
    let pauseTimer;

    const clearPauseTimer = () => {
      if (!pauseTimer) return;
      window.clearTimeout(pauseTimer);
      pauseTimer = undefined;
    };

    const preload = () => {
      if (this.hasAttribute('data-media-loaded') || this.hasAttribute('data-media-loading')) return;
      this.loadContent(false, false);
    };

    if (!('IntersectionObserver' in window)) {
      this.loadContent(false);
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return;
    }

    const preloadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        preloadObserver.disconnect();
        preload();
      },
      { rootMargin: isMobile ? '700px 0px' : '1000px 0px', threshold: 0.01 }
    );

    const playbackObserver = new IntersectionObserver(
      (entries) => {
        visibleRatio = entries[entries.length - 1]?.intersectionRatio ?? visibleRatio;

        if (visibleRatio >= playRatio) {
          clearPauseTimer();
          preload();
          if (!this.isPlaying) this.playMedia();
          return;
        }

        if (
          visibleRatio <= pauseRatio &&
          this.hasAttribute('data-media-loaded') &&
          this.isPlaying &&
          !pauseTimer
        ) {
          pauseTimer = window.setTimeout(() => {
            pauseTimer = undefined;
            if (visibleRatio <= pauseRatio && this.isPlaying) {
              this.pauseMedia();
            }
          }, pauseDelay);
        }
      },
      { threshold: [0, 0.08, 0.12, 0.15, 0.25] }
    );

    const handleVisibilityChange = () => {
      clearPauseTimer();

      if (document.hidden) {
        if (this.querySelector('video, iframe') && this.isPlaying) {
          this.pauseMedia();
        }
      } else if (visibleRatio >= playRatio && this.querySelector('video, iframe') && !this.isPlaying) {
        this.playMedia();
      }
    };

    preloadObserver.observe(this);
    playbackObserver.observe(this);
    document.addEventListener('visibilitychange', handleVisibilityChange, { signal });
    signal.addEventListener(
      'abort',
      () => {
        clearPauseTimer();
        preloadObserver.disconnect();
        playbackObserver.disconnect();
      },
      { once: true }
    );
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    if (this.hasAttribute('data-media-loaded') || this.hasAttribute('data-media-loading')) {
      this.playMedia();
      return;
    }

    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {boolean} [autoplay] - Whether to start autoplay while loading
   */
  loadContent(focus = true, autoplay = true) {
    if (this.hasAttribute('data-media-loaded') || this.hasAttribute('data-media-loading')) return;

    if (autoplay) {
      this.dispatchEvent(new MediaStartedPlayingEvent(this));
    }

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    const waitForFirstFrame = this.hasAttribute('data-defer-autoplay') && content instanceof HTMLVideoElement;

    if (!autoplay && content instanceof HTMLVideoElement) {
      content.removeAttribute('autoplay');
    }

    if (waitForFirstFrame) {
      content.preload = autoplay ? 'auto' : 'metadata';
      this.setAttribute('data-media-loading', 'true');
    } else {
      this.setAttribute('data-media-loaded', 'true');
    }

    const managedYouTube = this.hasAttribute('data-scroll-youtube') && content instanceof HTMLIFrameElement;
    if (managedYouTube) {
      const url = new URL(content.src);
      url.searchParams.set('autoplay', '0');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('enablejsapi', '1');
      url.searchParams.set('origin', window.location.origin);
      content.src = url.toString();
    }
    this.appendChild(content);
    if (managedYouTube) this.#connectYouTube(content);

    const revealMedia = () => {
      this.removeAttribute('data-media-loading');
      this.setAttribute('data-media-loaded', 'true');
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    };

    if (waitForFirstFrame) {
      if (content.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        revealMedia();
      } else {
        content.addEventListener('loadeddata', revealMedia, { once: true });
        content.addEventListener('playing', revealMedia, { once: true });
      }
    } else if (!managedYouTube) {
      revealMedia();
    }

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (autoplay && content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      this.playMedia();
    }
  }

  async #connectYouTube(iframe) {
    if (!window.YT?.Player) {
      window.respectoYouTubeReady ??= new Promise((resolve, reject) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => { previous?.(); resolve(); };
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      try { await window.respectoYouTubeReady; } catch { return; }
    }
    if (!this.isConnected || !iframe.isConnected) return;
    this.#youtubePlayer = new window.YT.Player(iframe, {
      events: {
        onReady: ({ target }) => {
          target.mute();
          this.dataset.playerReady = 'true';
          if (this.isPlaying && !document.hidden) target.playVideo();
          else target.pauseVideo();
        },
        onStateChange: ({ data }) => {
          this.dataset.playerState = String(data);
          if (data === 1) this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
          if (data === 1 && (!this.isPlaying || document.hidden)) this.#youtubePlayer.pauseVideo();
        },
        onError: ({ data }) => { this.dataset.playerError = String(data); }
      }
    });
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        // Safari can leave a deferred video at NETWORK_EMPTY after a metadata preload.
        video.preload = 'auto';
        if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
        const playPromise = video.play();
        playPromise?.catch((error) => {
          this.isPlaying = false;
          this.updatePlayPauseHint(false);
          if (error?.name !== 'AbortError' && this.hasAttribute('data-media-loading')) {
            // Autoplay can be refused on mobile (for example in Low Power Mode).
            // Show the native control so a user gesture can still start the video.
            video.controls = true;
            this.removeAttribute('data-media-loading');
            this.setAttribute('data-media-loaded', 'true');
            this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
          }
        });
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

